import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getDeviceBrandList(
    $filter: deviceBrandFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getDeviceBrandList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        deviceType
        name
        id
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.DeviceBrandListState = {
  result: null,
  error: null,
  loading: false
};

export const getDeviceBrandListRequest = createAsyncThunk<
  TYPES.DeviceBrandList,
  {
    filter?: TYPES.DeviceBrandFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'device_brand/get_device_brand_list',
  async (
  params: {
    filter?: TYPES.DeviceBrandFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getDeviceBrandList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getDeviceBrandListSlice = createSlice({
  name: 'get_device_brand_list',
  initialState: initialState,
  reducers: {
    getDeviceBrandListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getDeviceBrandListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getDeviceBrandListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getDeviceBrandListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getDeviceBrandListClear } =
getDeviceBrandListSlice.actions;

export const getDeviceBrandList = createSelector(
  (state: RootState) => state.deviceBrand.getDeviceBrandList.result,
  (state: RootState) => state.deviceBrand.getDeviceBrandList.error,
  (state: RootState) => state.deviceBrand.getDeviceBrandList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getDeviceBrandListSlice.reducer;